import { isPlainObject } from 'hooks/useStoredFilterValues/isPlanObject';
import { useCallback } from 'react';
import { useSearchParams } from '../query';

/**
 * Хук для хранения и восстановления значений фильтров в `localStorage`.
 * Значения сохраняются только если представляют собой (массив) простых объектов.
 *
 * @returns Методы для чтения и записи значений фильтра в storage
 */
export const useStoredFilterValues = () => {
  const { hasParams } = useSearchParams();

  const composeFilterKey = (key: string) => `${key}-filter-option`;

  const getFilterValuesFromStorage = (value: Record<string, unknown>): Record<string, unknown> =>
    Object.fromEntries(
      Object.entries(value).map(([key, value]) => {
        const savedValue = localStorage.getItem(composeFilterKey(key));

        return hasParams(key) && savedValue ? [key, JSON.parse(savedValue)] : [key, value];
      }),
    );

  const setFilterValuesToStorage = useCallback((values: Record<string, unknown>) => {
    Object.entries(values).forEach(([key, value]) => {
      const filterKey = composeFilterKey(key);

      const isValidValue =
        value && (Array.isArray(value) ? value.every(isPlainObject) : isPlainObject(value));

      isValidValue
        ? localStorage.setItem(filterKey, JSON.stringify(value))
        : localStorage.removeItem(filterKey);
    });
  }, []);

  return {
    getFilterValuesFromStorage,
    setFilterValuesToStorage,
  };
};
